/**
 * Zero-Click Dashboard - Maximum utility, minimum interaction
 * Everything accessible instantly via hover, keyboard, or smart defaults
 */

import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import { trpc } from '../lib/trpc'
import { useAuthStore } from '../stores/auth-store'
import { useUIStore } from '../stores/ui-store'
import { 
  Command, Search, Plus, Calendar, MessageSquare, 
  FileText, KanbanSquare, Clock, TrendingUp, 
  CheckCircle2, AlertCircle, Zap, Sparkles,
  ArrowRight, BarChart3, Target, Users
} from 'lucide-react'
import { cn } from '../lib/utils'

// Keyboard shortcut registry
const SHORTCUTS = {
  't': 'quickTask',
  'n': 'quickNote', 
  's': 'focusSearch',
  'k': 'openCommandPalette',
  'space': 'toggleFocus',
  '1-9': 'jumpToPanel',
  'Tab': 'nextPanel',
  'Enter': 'executeDefault'
} as const

export default function ZeroClickDashboard() {
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const searchRef = useRef<HTMLInputElement>(null)
  const [activeHover, setActiveHover] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [commandMode, setCommandMode] = useState(false)
  
  // Data queries
  const { data: workspace } = trpc.workspace.get.useQuery(
    { id: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: recentTasks = [] } = trpc.dashboard.recentTasks.useQuery(
    { workspaceId: workspaceId!, limit: 5 },
    { enabled: !!workspaceId }
  )
  
  const { data: todayTasks = [] } = trpc.dashboard.todayTasks.useQuery(
    { workspaceId: workspaceId! },
    { enabled: !!workspaceId }
  )
  
  const { data: activity = [] } = trpc.activity.list.useQuery(
    { workspaceId: workspaceId!, limit: 10 },
    { enabled: !!workspaceId }
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        if (e.key === 'Escape') {
          (e.target as HTMLElement).blur()
        }
        return
      }

      // Global shortcuts
      switch(e.key) {
        case 't':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            quickCreateTask()
          }
          break
        case 'n':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            quickCreateNote()
          }
          break
        case 's':
        case '/':
          e.preventDefault()
          searchRef.current?.focus()
          break
        case 'k':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault()
            setCommandMode(true)
          }
          break
        case ' ':
          if (e.shiftKey) {
            e.preventDefault()
            toggleFocusMode()
          }
          break
      }
      
      // Number keys for panel jumping
      if (e.key >= '1' && e.key <= '9' && !e.metaKey && !e.ctrlKey) {
        const panelIndex = parseInt(e.key) - 1
        jumpToPanel(panelIndex)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [workspaceId])

  // Quick actions (no modal, instant creation)
  const quickCreateTask = async () => {
    const title = prompt('Task title (or type "command" for full syntax):')
    if (!title) return
    
    // Parse natural language
    const parsed = parseTaskCommand(title)
    await createTask(parsed)
  }

  const quickCreateNote = async () => {
    // Create note instantly with timestamp title
    const timestamp = new Date().toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    })
    navigate(`/workspace/${workspaceId}/notes/new?title=${timestamp}`)
  }

  const parseTaskCommand = (input: string) => {
    // Smart parsing: "Fix bug today #high @john due:tomorrow"
    const result = {
      title: input,
      priority: 'medium',
      assignee: null,
      dueDate: null,
      tags: []
    }
    
    // Extract priority
    if (input.includes('#high') || input.includes('!')) {
      result.priority = 'high'
      result.title = result.title.replace(/#high|!/g, '').trim()
    }
    
    // Extract due date
    const dueMatch = input.match(/due:(\w+)/)
    if (dueMatch) {
      result.dueDate = parseDateShorthand(dueMatch[1])
      result.title = result.title.replace(/due:\w+/g, '').trim()
    }
    
    return result
  }

  const parseDateShorthand = (shorthand: string): Date | null => {
    const today = new Date()
    switch(shorthand.toLowerCase()) {
      case 'today':
        return today
      case 'tomorrow':
        return new Date(today.getTime() + 24 * 60 * 60 * 1000)
      case 'monday':
      case 'tuesday':
      case 'wednesday':
      case 'thursday':
      case 'friday':
        // Next occurrence of weekday
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
        const targetDay = days.indexOf(shorthand.toLowerCase())
        const currentDay = today.getDay()
        const daysUntil = (targetDay - currentDay + 7) % 7 || 7
        return new Date(today.getTime() + daysUntil * 24 * 60 * 60 * 1000)
      default:
        return null
    }
  }

  const createTaskMutation = trpc.dashboard.quickCreateTask.useMutation()
  
  const createTask = async (parsed: any) => {
    try {
      await createTaskMutation.mutateAsync({
        workspaceId: workspaceId!,
        title: parsed.title,
        priority: parsed.priority,
        dueDate: parsed.dueDate,
        assigneeId: parsed.assignee
      })
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  const jumpToPanel = (index: number) => {
    const panels = document.querySelectorAll('[data-panel-index]')
    if (panels[index]) {
      panels[index].scrollIntoView({ behavior: 'smooth', block: 'center' })
      ;(panels[index] as HTMLElement).focus()
    }
  }

  const toggleFocusMode = () => {
    document.body.classList.toggle('focus-mode')
  }

  // Smart search with instant results
  const searchResults = trpc.dashboard.instantSearch.useQuery(
    { query: searchQuery, workspaceId: workspaceId! },
    { 
      enabled: !!searchQuery && searchQuery.length > 1,
      staleTime: 5000
    }
  )

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Instant Access Bar - Always visible */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2 px-4 py-2">
          {/* Universal Search - Primary Focus */}
          <div className="flex-1 max-w-2xl relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search or type command... (Press 's' anytime)"
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg 
                       focus:bg-white/10 focus:border-krushr-blue-light/50 focus:outline-none
                       text-sm placeholder:text-white/30"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery) {
                  // Execute search or command
                  if (searchQuery.startsWith('/')) {
                    // Command mode
                    executeCommand(searchQuery.slice(1))
                  } else {
                    // Search mode
                    navigate(`/workspace/${workspaceId}/search?q=${searchQuery}`)
                  }
                }
              }}
            />
            
            {/* Instant search results dropdown */}
            {searchResults.data && searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#111111] border border-white/10 rounded-lg shadow-2xl max-h-96 overflow-y-auto">
                {searchResults.data.tasks?.map((task: any) => (
                  <button
                    key={task.id}
                    onClick={() => navigate(`/workspace/${workspaceId}/task/${task.id}`)}
                    className="w-full px-4 py-2 text-left hover:bg-white/5 flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-krushr-blue-light" />
                    <span className="flex-1">{task.title}</span>
                    <span className="text-xs text-white/40">{task.status}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions - Zero Click */}
          <div className="flex items-center gap-1">
            <QuickActionButton
              icon={Plus}
              label="Task"
              shortcut="t"
              onClick={quickCreateTask}
              onHover={() => setActiveHover('task')}
            />
            <QuickActionButton
              icon={FileText}
              label="Note"
              shortcut="n"
              onClick={quickCreateNote}
              onHover={() => setActiveHover('note')}
            />
            <QuickActionButton
              icon={Calendar}
              label="Event"
              shortcut="e"
              onClick={() => navigate(`/workspace/${workspaceId}/calendar`)}
              onHover={() => setActiveHover('event')}
            />
            <QuickActionButton
              icon={MessageSquare}
              label="Chat"
              shortcut="c"
              onClick={() => navigate(`/workspace/${workspaceId}/chat`)}
              onHover={() => setActiveHover('chat')}
            />
            <QuickActionButton
              icon={Command}
              label="Command"
              shortcut="⌘k"
              onClick={() => setCommandMode(true)}
              onHover={() => setActiveHover('command')}
            />
          </div>

          {/* Live Status Indicators */}
          <div className="flex items-center gap-3 ml-auto">
            <StatusIndicator
              icon={Clock}
              value={todayTasks.length}
              label="Today"
              color="blue"
              onClick={() => navigate(`/workspace/${workspaceId}/today`)}
            />
            <StatusIndicator
              icon={AlertCircle}
              value={recentTasks.filter(t => t.priority === 'high').length}
              label="Urgent"
              color="red"
              onClick={() => navigate(`/workspace/${workspaceId}/urgent`)}
            />
            <StatusIndicator
              icon={TrendingUp}
              value={activity.length}
              label="Activity"
              color="green"
              onClick={() => navigate(`/workspace/${workspaceId}/activity`)}
            />
          </div>
        </div>

        {/* Contextual Helper - Shows on hover */}
        {activeHover && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-white/10 backdrop-blur-xl rounded-lg text-xs">
            Press '{SHORTCUTS[activeHover as keyof typeof SHORTCUTS]}' anytime to {activeHover}
          </div>
        )}
      </div>

      {/* Main Dashboard Content */}
      <div className="pt-16 p-6">
        {/* Smart Cards Grid - Auto-arranged by importance */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Priority Tasks - Always First */}
          <SmartCard
            title="Priority Tasks"
            icon={Zap}
            data={recentTasks.filter(t => t.priority === 'high')}
            renderItem={(task) => (
              <TaskQuickView
                task={task}
                onClick={() => navigate(`/workspace/${workspaceId}/task/${task.id}`)}
                onComplete={() => completeTask(task.id)}
              />
            )}
            emptyMessage="No urgent tasks"
            actions={[
              { label: 'View All', onClick: () => navigate(`/workspace/${workspaceId}/tasks`) }
            ]}
          />

          {/* Today's Focus */}
          <SmartCard
            title="Today's Focus"
            icon={Target}
            data={todayTasks}
            renderItem={(task) => (
              <TaskQuickView
                task={task}
                onClick={() => navigate(`/workspace/${workspaceId}/task/${task.id}`)}
                onComplete={() => completeTask(task.id)}
              />
            )}
            emptyMessage="No tasks for today"
            actions={[
              { label: 'Plan Day', onClick: () => navigate(`/workspace/${workspaceId}/planning`) }
            ]}
          />

          {/* Recent Activity */}
          <SmartCard
            title="Recent Activity"
            icon={BarChart3}
            data={activity.slice(0, 5)}
            renderItem={(item) => (
              <ActivityQuickView
                activity={item}
                onClick={() => handleActivityClick(item)}
              />
            )}
            emptyMessage="No recent activity"
            autoRefresh={30000} // Refresh every 30 seconds
          />

          {/* Team Updates */}
          <SmartCard
            title="Team Updates"
            icon={Users}
            data={[]} // Would fetch team updates
            renderItem={(update) => (
              <div className="text-sm">{update}</div>
            )}
            emptyMessage="No team updates"
          />
        </div>

        {/* Floating Panels - Draggable, Resizable */}
        <FloatingPanels workspaceId={workspaceId!} />
      </div>

      {/* Command Palette Overlay */}
      {commandMode && (
        <CommandPalette
          onClose={() => setCommandMode(false)}
          onCommand={executeCommand}
        />
      )}
    </div>
  )
}

// Reusable Components

function QuickActionButton({ icon: Icon, label, shortcut, onClick, onHover }: any) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      className="p-2 hover:bg-white/10 rounded-lg transition-colors group relative"
      title={`${label} (${shortcut})`}
    >
      <Icon className="w-4 h-4 text-white/60 group-hover:text-white" />
      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-black/80 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        {label}
      </span>
    </button>
  )
}

function StatusIndicator({ icon: Icon, value, label, color, onClick }: any) {
  const colors = {
    blue: 'text-krushr-blue-light bg-krushr-blue-light/10',
    red: 'text-red-400 bg-red-400/10',
    green: 'text-green-400 bg-green-400/10'
  }
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors",
        colors[color as keyof typeof colors],
        "hover:bg-white/10"
      )}
    >
      <Icon className="w-4 h-4" />
      <span className="font-semibold">{value}</span>
      <span className="text-xs opacity-70">{label}</span>
    </button>
  )
}

function SmartCard({ title, icon: Icon, data, renderItem, emptyMessage, actions, autoRefresh }: any) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/[0.07] transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-white/60" />
          <h3 className="font-medium">{title}</h3>
        </div>
        {actions && (
          <div className="flex gap-1">
            {actions.map((action: any, i: number) => (
              <button
                key={i}
                onClick={action.onClick}
                className="text-xs text-white/40 hover:text-white"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        {data && data.length > 0 ? (
          data.map((item: any, i: number) => (
            <div key={i}>{renderItem(item)}</div>
          ))
        ) : (
          <p className="text-sm text-white/30">{emptyMessage}</p>
        )}
      </div>
    </div>
  )
}

function TaskQuickView({ task, onClick, onComplete }: any) {
  return (
    <div className="flex items-center gap-2 group">
      <button
        onClick={(e) => {
          e.stopPropagation()
          onComplete()
        }}
        className="w-4 h-4 rounded border border-white/20 hover:border-krushr-blue-light hover:bg-krushr-blue-light/20"
      />
      <button
        onClick={onClick}
        className="flex-1 text-left text-sm hover:text-krushr-blue-light"
      >
        {task.title}
      </button>
      {task.dueDate && (
        <span className="text-xs text-white/40">
          {new Date(task.dueDate).toLocaleDateString()}
        </span>
      )}
    </div>
  )
}

function ActivityQuickView({ activity, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left text-sm text-white/60 hover:text-white"
    >
      <span className="font-medium">{activity.user}</span> {activity.action} <span className="text-white/40">{activity.target}</span>
    </button>
  )
}

function FloatingPanels({ workspaceId }: { workspaceId: string }) {
  // Implementation for draggable floating panels
  return null
}

function CommandPalette({ onClose, onCommand }: any) {
  const [query, setQuery] = useState('')
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-[#111111] border border-white/10 rounded-xl w-full max-w-2xl">
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter') {
              onCommand(query)
              onClose()
            }
          }}
          placeholder="Type a command..."
          className="w-full px-6 py-4 bg-transparent text-lg focus:outline-none"
        />
        
        <div className="border-t border-white/10 p-4">
          <div className="text-xs text-white/40">
            Available: new task | new note | go to [place] | search [query] | focus mode | settings
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function completeTask(taskId: string) {
  // Implementation would update task status
  console.log('Complete task:', taskId)
}

function handleActivityClick(activity: any) {
  console.log('Activity clicked:', activity)
}

function executeCommand(command: string) {
  console.log('Execute command:', command)
  // Parse and execute various commands
}